import { useCallback, useEffect, useState } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'

export type LockerRoomCoach = {
  id: string
  name?: string | null
  email?: string | null
  avatar_url?: string | null
}

export type ProspectBudgetAllocation = {
  planned_annual_cents: number | null
  planned_monthly_cents: number | null
  spent_cents: number | null
  remaining_cents: number | null
  annual_limit_cents: number | null
  monthly_limit_cents: number | null
  soft_monthly_limit_cents: number | null
}

export type LockerRoomMemberProspect = {
  id: number
  name?: string | null
  avatar_url?: string | null
  status?: string | null
}

export type ProspectLockerRoom = {
  id: string
  name: string | null
  public_name: string | null
  coach_user_id: string | null
  share_code?: string | null
  created_at?: string | null
}

export type LockerRoomCredit = {
  balance_credits: number
  updated_at?: string | null
}

export type ProspectLockerRoomState = {
  loading: boolean
  error: string
  lockerRoom: ProspectLockerRoom | null
  coach: LockerRoomCoach | null
  budget: ProspectBudgetAllocation | null
  credits: LockerRoomCredit | null
  teammates: LockerRoomMemberProspect[]
  prospectId: number | null
}

const INITIAL_STATE: ProspectLockerRoomState = {
  loading: true,
  error: '',
  lockerRoom: null,
  coach: null,
  budget: null,
  credits: null,
  teammates: [],
  prospectId: null,
}

// Default budget constants for prospects
export const PROSPECT_DEFAULT_ANNUAL_CENTS = 120000 // $1,200
export const PROSPECT_DEFAULT_MONTHLY_CENTS = 10000 // $100

/**
 * Hook for prospects to load their locker room context
 * This is the prospect's view of their coach's locker room
 */
export function useProspectLockerRoom() {
  const { user, session } = useAuthContext() as any
  const [state, setState] = useState<ProspectLockerRoomState>(INITIAL_STATE)

  const loadLockerRoomContext = useCallback(async () => {
    if (!user?.id) {
      setState({ ...INITIAL_STATE, loading: false })
      return
    }

    setState((prev) => ({ ...prev, loading: true, error: '' }))

    try {
      // Step 1: Find the prospect record for this user (check both owner_id and user_id)
      let prospectData: any = null
      let prospectError: any = null

      // Try user_id first (set by nomination flow)
      const { data: byUserId, error: err1 } = await supabase
        .from('prospects')
        .select('id, name, coach_user_id, status')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (!err1 && byUserId) {
        prospectData = byUserId
      } else {
        // Fallback to owner_id (legacy)
        const { data: byOwnerId, error: err2 } = await supabase
          .from('prospects')
          .select('id, name, coach_user_id, status')
          .eq('owner_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
        prospectData = byOwnerId
        prospectError = err2
      }

      if (prospectError) throw prospectError

      if (!prospectData) {
        setState({
          ...INITIAL_STATE,
          loading: false,
          error: 'No prospect profile found. Ask your coach to invite you.',
        })
        return
      }

      const prospectId = prospectData.id as number

      // Step 2: Find the locker room this prospect belongs to
      const { data: memberData, error: memberError } = await supabase
        .from('locker_room_members')
        .select('locker_room_id, role, active')
        .eq('prospect_id', prospectId)
        .eq('role', 'athlete')
        .eq('active', true)
        .limit(1)
        .maybeSingle()

      if (memberError) throw memberError

      if (!memberData?.locker_room_id) {
        setState({
          ...INITIAL_STATE,
          loading: false,
          prospectId,
          error: 'You are not currently linked to a locker room. Ask your coach to add you.',
        })
        return
      }

      const lockerRoomId = memberData.locker_room_id as string

      // Step 3: Load locker room, coach profile, budget, credits, and teammates in parallel
      const [lockerRoomResult, budgetResult, creditsResult, teammatesResult, allocationResult] = await Promise.all([
        // Locker room details
        supabase
          .from('locker_rooms')
          .select('id, name, public_name, coach_user_id, share_code, created_at')
          .eq('id', lockerRoomId)
          .maybeSingle(),

        // Prospect's individual budget
        supabase
          .from('prospect_budgets')
          .select('annual_limit_cents, monthly_limit_cents, soft_monthly_limit_cents, spent_cents, remaining_cents')
          .eq('prospect_id', prospectId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),

        // Locker room credits pool
        supabase
          .from('locker_room_credits')
          .select('balance_credits, updated_at')
          .eq('locker_room_id', lockerRoomId)
          .maybeSingle(),

        // Teammates in the same locker room (load IDs, then fetch prospects separately)
        supabase
          .from('locker_room_members')
          .select('prospect_id')
          .eq('locker_room_id', lockerRoomId)
          .eq('role', 'athlete')
          .eq('active', true)
          .neq('prospect_id', prospectId),

        // Budget allocation from coach
        supabase
          .from('locker_room_budget_allocations')
          .select('planned_annual_cents, planned_monthly_cents')
          .eq('locker_room_id', lockerRoomId)
          .eq('prospect_id', prospectId)
          .maybeSingle(),
      ])

      if (lockerRoomResult.error) throw lockerRoomResult.error

      const lockerRoom = lockerRoomResult.data as ProspectLockerRoom | null

      // Step 4: Load coach profile if we have a coach_user_id
      let coach: LockerRoomCoach | null = null
      if (lockerRoom?.coach_user_id) {
        const { data: coachData } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url')
          .eq('id', lockerRoom.coach_user_id)
          .maybeSingle()

        if (coachData) {
          coach = coachData as LockerRoomCoach
        }
      }

      // Merge budget data with allocation
      const budgetData = budgetResult.data as Record<string, number | null> | null
      const allocationData = allocationResult.data as Record<string, number | null> | null

      const budget: ProspectBudgetAllocation = {
        planned_annual_cents: allocationData?.planned_annual_cents ?? budgetData?.annual_limit_cents ?? PROSPECT_DEFAULT_ANNUAL_CENTS,
        planned_monthly_cents: allocationData?.planned_monthly_cents ?? budgetData?.soft_monthly_limit_cents ?? PROSPECT_DEFAULT_MONTHLY_CENTS,
        spent_cents: budgetData?.spent_cents ?? 0,
        remaining_cents: budgetData?.remaining_cents ?? null,
        annual_limit_cents: budgetData?.annual_limit_cents ?? PROSPECT_DEFAULT_ANNUAL_CENTS,
        monthly_limit_cents: budgetData?.monthly_limit_cents ?? PROSPECT_DEFAULT_MONTHLY_CENTS,
        soft_monthly_limit_cents: budgetData?.soft_monthly_limit_cents ?? PROSPECT_DEFAULT_MONTHLY_CENTS,
      }

      // Calculate remaining if not set
      if (budget.remaining_cents === null && budget.annual_limit_cents !== null) {
        budget.remaining_cents = Math.max((budget.annual_limit_cents ?? 0) - (budget.spent_cents ?? 0), 0)
      }

      const credits = creditsResult.data as LockerRoomCredit | null

      // Extract teammates — load prospect details separately
      const teammateIds = (teammatesResult.data || []).map((row: any) => row.prospect_id).filter(Boolean)
      let teammates: LockerRoomMemberProspect[] = []
      if (teammateIds.length) {
        const { data: tmProspects } = await supabase
          .from('prospects')
          .select('id, name, avatar_url, status')
          .in('id', teammateIds)
        teammates = (tmProspects || []) as LockerRoomMemberProspect[]
      }

      setState({
        loading: false,
        error: '',
        lockerRoom,
        coach,
        budget,
        credits,
        teammates,
        prospectId,
      })
    } catch (err: any) {
      console.error('[useProspectLockerRoom] error', err)
      setState({
        ...INITIAL_STATE,
        loading: false,
        error: err?.message || 'Unable to load locker room',
      })
    }
  }, [user?.id])

  useEffect(() => {
    let active = true
    const load = async () => {
      if (!active) return
      await loadLockerRoomContext()
    }
    load()
    return () => {
      active = false
    }
  }, [loadLockerRoomContext])

  return {
    ...state,
    reload: loadLockerRoomContext,
  }
}
